/**
 * Table Responsive Utility
 * 
 * Provides utilities for handling responsive tables with dropdown menus.
 * Fixes dropdown overflow issues when table has only one row.
 * 
 * @example
 * ```typescript
 * import { TableResponsiveUtil } from '@core/services/util';
 * 
 * displayMemos = computed(() => {
 *   return TableResponsiveUtil.addEmptyRowsIfNeeded(this.filteredMemos(), 2);
 * });
 * ```
 */
export class TableResponsiveUtil {
  /**
   * Adds empty placeholder rows to an array based on item count.
   * This prevents dropdown menus from being clipped by table overflow.
   * 
   * Rules:
   * - 1 data row → adds 2 empty rows
   * - 2 data rows → adds 1 empty row
   * - 3+ data rows → no empty rows added
   * 
   * @param items - Array of items
   * @returns Array with empty rows added if needed
   * 
   * @example
   * ```typescript
   * // 1 item → adds 2 empty rows
   * const items1 = [{ id: 1, name: 'Item 1' }];
   * const result1 = TableResponsiveUtil.addEmptyRowsIfNeeded(items1);
   * // Returns: [{ id: 1, name: 'Item 1' }, { id: 'empty-1', isEmpty: true }, { id: 'empty-2', isEmpty: true }]
   * 
   * // 2 items → adds 1 empty row
   * const items2 = [{ id: 1 }, { id: 2 }];
   * const result2 = TableResponsiveUtil.addEmptyRowsIfNeeded(items2);
   * // Returns: [{ id: 1 }, { id: 2 }, { id: 'empty-1', isEmpty: true }]
   * ```
   */
  static addEmptyRowsIfNeeded<T extends { id?: string | number }>(
    items: T[]
  ): (T & { isEmpty?: boolean })[] {
    if (items.length === 1) {
      // 1 data row → add 2 empty rows
      const emptyRows = Array.from({ length: 2 }, (_, index) => ({
        id: `empty-${index + 1}`,
        isEmpty: true
      } as T & { isEmpty: boolean }));
      return [...items, ...emptyRows];
    } else if (items.length === 2) {
      // 2 data rows → add 1 empty row
      const emptyRow = {
        id: 'empty-1',
        isEmpty: true
      } as T & { isEmpty: boolean };
      return [...items, emptyRow];
    }
    // 3+ data rows → no empty rows needed
    return items;
  }

  /**
   * Checks if an item is an empty placeholder row
   * 
   * @param item - Item to check
   * @returns True if item is an empty placeholder
   */
  static isEmptyRow(item: any): boolean {
    return item?.isEmpty === true || (item?.id && String(item.id).startsWith('empty-'));
  }

  /**
   * Filters out empty placeholder rows from an array
   * 
   * @param items - Array of items that may contain empty rows
   * @returns Array without empty rows
   */
  static filterEmptyRows<T>(items: (T & { isEmpty?: boolean })[]): T[] {
    return items.filter(item => !this.isEmptyRow(item)) as T[];
  }
}

